import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS, API_CONFIG } from '../config/api.config';
import { saveDeductedLocation, getDeductedLocation } from '../utils/locationStorage';

/* ── Fonts ───────────────────────────────────────────────────── */
const fontLink = document.createElement('link');
fontLink.rel = 'stylesheet';
fontLink.href = 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Lato:wght@400;700&display=swap';
document.head.appendChild(fontLink);

/* ── Category Config ─────────────────────────────────────────── */
/* ── Filter Configuration ───────────────────────────────────── */
const FILTER_CONFIG = {
  enableAutoAreaFilter: false, // Set to true to enable auto-applying area filter based on location detection
};

/* ── Default category styling (from API data) ────────────────────── */
const DEFAULT_CAT_STYLE = { icon: '🛠️', color: '#f1f5f9', accent: '#64748b' };

/* ── Get category styling from API data or use defaults ──────────── */
const getCategoryStyle = (category) => {
  if (!category) return DEFAULT_CAT_STYLE;
  return {
    icon: category.icon || DEFAULT_CAT_STYLE.icon,
    color: category.color || DEFAULT_CAT_STYLE.color,
    accent: category.accent || DEFAULT_CAT_STYLE.accent,
  };
};

/* ── Get pricing type display (icon + label) ──────────────────────── */
const getPricingTypeDisplay = (pricingType) => {
  const pricingMap = {
    visiting: { icon: '🚗', label: 'Visiting Charges' },
    fixed: { icon: '✅', label: 'Fixed Price' },
    starting: { icon: '📈', label: 'Starting Price' },
    subscription: { icon: '🔄', label: 'Subscription' },
  };
  return pricingMap[pricingType] || { icon: '💰', label: 'Price' };
};

/* ════════════════════════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════════════════════════ */
const Services = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);
  const searchRef = useRef(null);

  /* state */
  const [activeTab, setActiveTab] = useState('All');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({ category_id: '', city_id: '', area_id: '', radius: 60 }); // radius in km

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [quickTabs, setQuickTabs] = useState([{ label: 'All', icon: '🏠' }]);
  const itemsPerPage = 12;

  const [userCity, setUserCity] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [cityManual, setCityManual] = useState(false);
  const [deductedLocation, setDeductedLocation] = useState(null);

  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  /* ── Init - OPTIMIZED for parallel loading ─────────────────────────────── */
  useEffect(() => {
    // Load all non-dependent data in parallel
    Promise.all([
      fetchCategoriesOptimized(),
      fetchCitiesOptimized(),
      fetchAreasOptimized(),
    ]).then(() => {
      // After main data loads, get user location
      getUserLocation();
    });

    // Apply URL parameters
    const catFromUrl = searchParams.get('category_id');
    const cityFromUrl = searchParams.get('city_id');
    const areaFromUrl = searchParams.get('area_id');
    const searchFromUrl = searchParams.get('search');

    if (catFromUrl) setFilters(p => ({ ...p, category_id: catFromUrl }));
    if (cityFromUrl) setFilters(p => ({ ...p, city_id: cityFromUrl }));
    if (areaFromUrl) setFilters(p => ({ ...p, area_id: areaFromUrl }));
    if (searchFromUrl) setSearchText(searchFromUrl);
  }, []);

  useEffect(() => {
    if (!categories.length || !filters.category_id) return;
    const child = categories.find(c => c.id == filters.category_id);
    if (child?.parent_id) setExpandedCats(p => new Set([...p, child.parent_id]));
  }, [filters.category_id, categories]);

  useEffect(() => {
    if (userCity && cities.length && !filters.city_id && !cityManual) {
      const m = cities.find(c =>
        c.name.toLowerCase().includes(userCity.toLowerCase()) ||
        userCity.toLowerCase().includes(c.name.toLowerCase())
      );
      if (m) {
        setFilters(p => ({ ...p, city_id: m.id }));
        // Auto-apply area if deducted location has it
        if (deductedLocation?.area_id) {
          setFilters(p => ({ ...p, city_id: m.id, area_id: deductedLocation.area_id }));
        }
      }
    }
  }, [userCity, cities, deductedLocation?.area_id]);

  useEffect(() => { setCurrentPage(1); fetchServices(1); }, [filters, searchText]);

  /* ── Fetchers (OPTIMIZED with caching) ─────────────────────────────────── */
  
  // Cache with TTL (5 minutes = 300000ms)
  const dataCache = useRef({ categories: null, cities: null, areas: null, timestamp: {} });
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  const isCacheValid = (key) => {
    const cached = dataCache.current[key];
    const time = dataCache.current.timestamp[key];
    return cached && time && (Date.now() - time < CACHE_TTL);
  };

  const fetchCategoriesOptimized = async () => {
    if (isCacheValid('categories')) {
      setCategories(dataCache.current.categories);
      return dataCache.current.categories;
    }
    try {
      const data = await httpClient.get(ENDPOINTS.CATEGORIES, null, { useCache: true });
      dataCache.current.categories = data;
      dataCache.current.timestamp.categories = Date.now();
      setCategories(data);
      // Generate quick tabs from parent categories
      const parents = data.filter(c => !c.parent_id).slice(0, 5);
      setQuickTabs([
        { label: 'All', icon: '🏠' },
        ...parents.map(p => ({ label: p.name, icon: p.icon || '🛠️' }))
      ]);
      return data;
    } catch (e) {
      console.error('Error fetching categories:', e);
      return [];
    }
  };

  const fetchCitiesOptimized = async () => {
    if (isCacheValid('cities')) {
      setCities(dataCache.current.cities);
      return dataCache.current.cities;
    }
    try {
      const data = await httpClient.get(ENDPOINTS.CITIES, null, { useCache: true });
      dataCache.current.cities = data;
      dataCache.current.timestamp.cities = Date.now();
      setCities(data);
      return data;
    } catch (e) {
      console.error('Error fetching cities:', e);
      return [];
    }
  };

  const fetchAreasOptimized = async () => {
    if (isCacheValid('areas')) {
      setAreas(dataCache.current.areas);
      return dataCache.current.areas;
    }
    try {
      const data = await httpClient.get(ENDPOINTS.AREAS, null, { useCache: true });
      dataCache.current.areas = data;
      dataCache.current.timestamp.areas = Date.now();
      setAreas(data);
      return data;
    } catch (e) {
      console.error('Error fetching areas:', e);
      return [];
    }
  };

  const fetchServices = async (page = 1) => {
    const rid = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = {
        page,
        per_page: itemsPerPage,
        ...(filters.category_id && { category_id: filters.category_id }),
        ...(filters.city_id && { city_id: filters.city_id }),
        ...(filters.area_id && { area_id: filters.area_id }),
        ...(filters.radius && { radius: filters.radius }),
        ...(searchText.trim() && { search: searchText.trim() }),
        ...(userLocation?.lat && { lat: userLocation.lat, lng: userLocation.lng }),
      };

      // Don't cache search results or filtered results
      const useCache = !searchText.trim() && !filters.category_id && !filters.city_id && !filters.area_id && filters.radius === 60;
      const data = await httpClient.get(ENDPOINTS.SERVICES, params, { useCache });

      if (rid === requestIdRef.current) {
        const services = data.data || data;
        const pagination = data.pagination;

        setServices(services);

        if (pagination) {
          setTotalCount(pagination.total);
          setTotalPages(pagination.last_page || Math.ceil(pagination.total / itemsPerPage));
        } else {
          setTotalCount(data.total || services.length);
          setTotalPages(Math.ceil((data.total || services.length) / itemsPerPage));
        }

        setLoading(false);
      }
    } catch (e) {
      console.error('Error fetching services:', e);
      setLoading(false);
    }
  };

  const getUserLocation = () => {
    // Check for previously saved location
    const saved = getDeductedLocation();
    if (saved) {
      setDeductedLocation(saved);
      setUserCity(saved.city_name);
      setUserLocation({ lat: saved.lat, lng: saved.lng });
      if (!cityManual) {
        setFilters(p => ({
          ...p,
          city_id: saved.city_id?.toString() || '',
          area_id: FILTER_CONFIG.enableAutoAreaFilter ? (saved.area_id?.toString() || '') : ''
        }));
      }
      return;
    }

    if (!navigator.geolocation) {
      setUserCity('Ghaziabad');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setUserLocation({ lat, lng });
        try {
          // Call backend location detection endpoint
          const response = await fetch(`${API_CONFIG.BASE_URL}/detect-location?lat=${lat}&lng=${lng}`);
          const data = await response.json();

          if (data.city_id && data.city_name) {
            // Save deducted location with timestamp
            saveDeductedLocation({
              city_id: data.city_id,
              city_name: data.city_name,
              area_id: data.area_id,
              area_name: data.area_name,
              lat,
              lng
            });
            setDeductedLocation(data);
            setUserCity(data.city_name);
            
            // Auto-apply filters
            if (!cityManual) {
              setFilters(p => ({
                ...p,
                city_id: data.city_id.toString(),
                area_id: FILTER_CONFIG.enableAutoAreaFilter && data.area_id ? data.area_id.toString() : ''
              }));
            }
          }
        } catch (err) {
          console.error('Location detection failed:', err);
          setUserCity('Your City');
        }
      },
      () => setUserCity('Ghaziabad')
    );
  };

  const fetchSuggestions = async (q) => {
    try {
      const data = await httpClient.get(ENDPOINTS.SEARCH_SUGGESTIONS, { q }, { useCache: false });
      const suggestions = (data.suggestions || []).map(s =>
        typeof s === 'object' ? s.text : s
      );
      setSuggestions(suggestions);
      setShowSug(true);
    } catch (e) {
      console.error('Error fetching suggestions:', e);
    }
  };

  /* ── Handlers ─────────────────────────────────────────────── */
  const handleSearchInput = (v) => {
    setInputValue(v);
    clearTimeout(debounceRef.current);
    if (v.trim().length > 1) debounceRef.current = setTimeout(() => fetchSuggestions(v.trim()), 400);
    else setShowSug(false);
  };

  const triggerSearch = () => { setSearchText(inputValue); setShowSug(false); };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    const name = tab === 'All' ? '' : tab;
    const cat = name ? categories.find(c => c.name === name) : null;
    setFilters(p => ({ ...p, category_id: cat?.id || '' }));
  };

  const clearAllFilters = () => {
    setCityManual(false);
    setFilters({ category_id: '', city_id: '', area_id: '', radius: 60 });
    setActiveTab('All');
    setInputValue('');
    setSearchText('');
  };

  const activeFilterCount = [filters.category_id, filters.city_id, filters.area_id, searchText, filters.radius !== 60].filter(Boolean).length;

  /* ── Render category tree (sidebar/drawer) ────────────────── */
  const renderCategoryTree = () => {
    const parents = categories.filter(c => !c.parent_id);
    const childMap = {};
    categories.forEach(c => { if (c.parent_id) { childMap[c.parent_id] = childMap[c.parent_id] || []; childMap[c.parent_id].push(c); } });

    return parents.map(p => {
      const cfg = getCategoryStyle(p);
      const expanded = expandedCats.has(p.id);
      const active = filters.category_id == p.id;
      return (
        <div key={p.id} style={{ marginBottom: 6 }}>
          <button
            onClick={() => {
              const s = new Set(expandedCats);
              s.has(p.id) ? s.delete(p.id) : s.add(p.id);
              setExpandedCats(s);
              setFilters(prev => ({ ...prev, category_id: p.id }));
            }}
            style={{ ...D.catBtn, background: active ? cfg.color : 'transparent', color: active ? cfg.accent : '#374151' }}
          >
            <span style={{ fontSize: 20 }}>{cfg.icon}</span>
            <span style={{ flex: 1, textAlign: 'left', fontWeight: active ? 800 : 600 }}>{p.name}</span>
            {p.service_count > 0 && <span style={{ ...D.badge, background: active ? cfg.accent : '#e5e7eb', color: active ? '#fff' : '#6b7280' }}>{p.service_count}</span>}
            {childMap[p.id] && <span style={{ fontSize: 12, color: '#9ca3af' }}>{expanded ? '▲' : '▼'}</span>}
          </button>
          {expanded && childMap[p.id] && (
            <div style={{ marginLeft: 20, marginTop: 2 }}>
              {childMap[p.id].map(ch => (
                <button key={ch.id} onClick={() => setFilters(prev => ({ ...prev, category_id: ch.id }))}
                  style={{ ...D.catChildBtn, ...(filters.category_id == ch.id ? { background: '#f0fdf4', color: '#16a34a', fontWeight: 700 } : {}) }}>
                  • {ch.name}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    });
  };
  /* ── Service Card (Optimized) ─────────────────────────────────── */
  const ServiceCard = ({ s }) => {
    const cfg = getCategoryStyle(s.category);
    const description = s.description || "Professional service with 100% satisfaction guarantee.";

    const EyeIcon = () => (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
      </svg>
    );

    return (
      <div className="svc-card" style={D.card} onClick={() => navigate(`/service/${s.id}`)}>
        {/* Top Row: Category + Rating */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ ...D.catChip, background: cfg.color, color: cfg.accent }}>
            {cfg.icon} {s.category?.name}
          </div>
          <div style={D.ratingChip}>★ {parseFloat(s.rating || 0).toFixed(1)}</div>
        </div>

        {/* Title Row: Title on Left, Beautiful Eye on Right */}
        <div style={D.titleRow}>
          <h3 style={D.cardTitle}>{s.name}</h3>
          <div className="view-trigger" style={D.titleEyeBadge} title="Quick View">
            <EyeIcon />
          </div>
        </div>

        {/* Description */}
        <p style={D.descriptionText}>{description}</p>

        {/* Location and Price Row */}
        <div style={D.metaInfoRow}>
          <div style={D.metaItem}>📍 {s.user?.area?.name || 'Nearby'}</div>
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px'}}>
            {s.pricing_type && (
              <div style={{fontSize: '0.6rem', fontWeight: '500', color: '#4f46e5'}}>
                {getPricingTypeDisplay(s.pricing_type).label}
              </div>
            )}
            <div style={D.priceText}>₹{s.price}</div>
          </div>
        </div>

        {/* Footer with Vendor and Contact Actions */}
        <div style={D.cardFooter}>
          <div style={D.vendorGroup}>
            <div style={D.avatarSmall}>{(s.user?.name || 'P')[0]}</div>
            <span style={D.vendorNameVisible}>{s.user?.name}</span>
          </div>

          <div style={D.actionButtonGroup}>
            <button style={D.iconBtnCall} title="Call" onClick={e => { e.stopPropagation(); window.open(`tel:${s.user?.phone}`); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
            </button>
            <button style={D.iconBtnWA} title="WhatsApp" onClick={e => { e.stopPropagation(); window.open(`https://wa.me/${s.user?.whatsapp}`); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.937 3.659 1.431 5.623 1.432h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"></path></svg>
            </button>
          </div>
        </div>
      </div>
    );
  };
  /* ── Skeleton ─────────────────────────────────────────────── */
  const Skeleton = () => (
    <div style={D.card}>
      {[100, 60, 40, 80].map((w, i) => (
        <div key={i} className="shimmer" style={{ height: i === 0 ? 16 : 14, width: `${w}%`, borderRadius: 8, marginBottom: 12, background: '#e5e7eb' }} />
      ))}
    </div>
  );

  /* ── Filter Sidebar / Drawer shared content ───────────────── */
  const FilterContent = () => (
    <div>
      <div style={D.filterSection}>
        <div style={D.filterLabel}>🎯 Search Radius</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 0 8px 0' }}>
          <input 
            type="range" 
            min="0.5" 
            max="100" 
            step="0.5" 
            value={filters.radius} 
            onChange={e => setFilters(p => ({ ...p, radius: parseFloat(e.target.value) }))}
            onTouchMove={e => e.preventDefault()}
            style={{ 
              flex: 1, 
              cursor: 'pointer',
              WebkitAppearance: 'slider-horizontal',
              appearance: 'slider-horizontal',
              width: '100%',
              height: 5,
              borderRadius: 5,
              background: '#e5e7eb',
              outline: 'none',
              WebkitSliderThumb: { WebkitAppearance: 'none' },
              MozRangeTrack: { background: '#e5e7eb', border: 'none', borderRadius: 5 },
              MozRangeThumb: { background: '#4f46e5', border: 'none', borderRadius: '50%', cursor: 'pointer', height: 14, width: 14 },
            }}
            className="range-slider"
          />
          <div style={{ 
            minWidth: 60, 
            padding: '4px 8px', 
            background: '#e0e7ff', 
            color: '#4338ca', 
            borderRadius: 4, 
            fontSize: 11, 
            fontWeight: 600,
            textAlign: 'center',
            flexShrink: 0
          }}>
            {filters.radius < 1 ? Math.round(filters.radius * 1000) + ' m' : filters.radius.toFixed(1) + ' km'}
          </div>
        </div>
      </div>
      <div style={D.filterDivider} />
      <div style={D.filterSection}>
        <div style={D.filterLabel}>🗂 Service Type</div>
        <div style={{ 
          maxHeight: 200, 
          overflowY: 'auto',
          paddingRight: 2,
          scrollbarWidth: 'thin',
          scrollbarColor: '#d1d5db #f9fafb'
        }}>
          {renderCategoryTree()}
        </div>
      </div>
      <div style={D.filterDivider} />
      <div style={D.filterSection}>
        <div style={D.filterLabel}>🏙 City</div>
        <select value={filters.city_id} onChange={e => {
          setCityManual(true);
          const cityId = e.target.value;
          setFilters(p => ({ 
            ...p, 
            city_id: cityId,
            area_id: '' // Reset area when city changes
          }));
        }} style={D.select}>
          <option value="">All Cities</option>
          {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div style={D.filterDivider} />
      <div style={D.filterSection}>
        <div style={D.filterLabel}>📌 Area / Locality</div>
        <select value={filters.area_id} onChange={e => setFilters(p => ({ ...p, area_id: e.target.value }))} style={D.select}>
          <option value="">All Areas</option>
          {areas
            .filter(a => !filters.city_id || a.city_id === parseInt(filters.city_id))
            .map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      {activeFilterCount > 0 && (
        <>
          <div style={D.filterDivider} />
          <button onClick={clearAllFilters} style={D.clearBtn}>✕ Clear All Filters</button>
        </>
      )}
    </div>
  );

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <div style={D.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Lato:wght@400;700&display=swap');
        * { box-sizing: border-box; font-family: 'Nunito', sans-serif; }
        body { margin: 0; background: #f9fafb; }
        .svc-card:hover { transform: translateY(-3px); box-shadow: 0 16px 32px rgba(0,0,0,0.1) !important; }
        .svc-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .tab-pill:hover { background: #e0e7ff !important; }
        .filter-btn:hover { background: #f3f4f6; }
        @keyframes shimmer { 0%{opacity:.6} 50%{opacity:1} 100%{opacity:.6} }
        .shimmer { animation: shimmer 1.4s infinite ease-in-out; }
        ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
        
        /* Range Slider Styling */
        .range-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 5px;
          background: linear-gradient(to right, #e5e7eb 0%, #e5e7eb 100%);
          outline: none;
          -webkit-tap-highlight-color: transparent;
        }
        .range-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #4f46e5;
          cursor: pointer;
          box-shadow: 0 1px 4px rgba(79, 70, 229, 0.4);
          transition: background 0.2s;
        }
        .range-slider::-webkit-slider-thumb:hover {
          background: #4338ca;
          box-shadow: 0 1px 6px rgba(67, 56, 202, 0.6);
        }
        .range-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #4f46e5;
          cursor: pointer;
          border: none;
          box-shadow: 0 1px 4px rgba(79, 70, 229, 0.4);
          transition: background 0.2s;
        }
        .range-slider::-moz-range-thumb:hover {
          background: #4338ca;
          box-shadow: 0 1px 6px rgba(67, 56, 202, 0.6);
        }
        .range-slider::-moz-range-track {
          background: transparent;
          border: none;
        }
        .range-slider::-moz-range-progress {
          background: #4f46e5;
          border-radius: 5px;
          height: 5px;
        }
        
        @media(max-width:768px){ 
          .desktop-sidebar{display:none!important;} 
          .show-mobile{display:flex!important;} 
          .header-search { width: 100% !important; max-width: 100% !important; margin-top: 6px !important; }
        }
        @media(min-width:769px){ .show-mobile{display:none!important;} }
        select:focus, input:focus { outline: none; }
        button { cursor: pointer; font-family: 'Nunito', sans-serif; }
      `}</style>

      {/* ── TOP HEADER ─────────────────────────────────────────── */}
      <header style={D.header}>

        {/* Brand + location row + search */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={D.brandTitle}>Find a Service Near You</div>
            <div style={D.locationLine}>
              <span>📍</span>
              <span style={{ color: '#4f46e5', fontWeight: 700 }}>{userCity || 'Detecting location…'}</span>
              {deductedLocation && <span style={{ fontSize: '10px', background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>✓ Auto-detected</span>}
              <span style={{ color: '#9ca3af' }}>• {totalCount} professionals ready to help</span>
            </div>
          </div>
          {/* ── SEARCH BAR (Desktop inline) ──────────────────────────────────────── */}
          <div style={{ position: 'relative', minWidth: '280px', width: '100%', maxWidth: '580px' }} ref={searchRef} className="header-search">
            <div style={{ ...D.searchBox, ...(searchFocused ? D.searchBoxFocused : {}) }}>
              <span style={{ fontSize: 16, marginLeft: 1 }}>🔍</span>
              <input
                type="text"
                placeholder="What do you need?"
                value={inputValue}
                onChange={e => handleSearchInput(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                onKeyDown={e => e.key === 'Enter' && triggerSearch()}
                style={D.searchInput}
              />
              {inputValue && (
                <button onClick={() => { setInputValue(''); setSearchText(''); setShowSug(false); }} style={D.clearX}>✕</button>
              )}
              <button onClick={triggerSearch} style={D.searchBtn}>Search</button>
            </div>

            {showSug && suggestions.length > 0 && (
              <div style={D.sugBox}>
                <div style={{ padding: '8px 16px', fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Suggestions</div>
                {suggestions.map((s, i) => (
                  <div key={i} onClick={() => { setInputValue(s); setSearchText(s); setShowSug(false); }} style={D.sugItem}>
                    🔍 {s}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── QUICK-PICK TABS ─────────────────────────────────── */}
        <div style={D.tabRow}>
          {quickTabs.map(t => (
            <button
              key={t.label}
              className="tab-pill"
              onClick={() => handleTabClick(t.label)}
              style={{
                ...D.tabPill,
                ...(activeTab === t.label ? D.tabPillActive : {}),
              }}
            >
              <span style={{ fontSize: 18 }}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* ── ACTIVE FILTER CHIPS ─────────────────────────────── */}
        {activeFilterCount > 0 && (
          <div style={D.chipRow}>
            {searchText && (
              <div style={D.chip}>
                🔍 "{searchText}"
                <button onClick={() => { setInputValue(''); setSearchText(''); }} style={D.chipX}>✕</button>
              </div>
            )}
            {filters.category_id && (
              <div style={D.chip}>
                {getCategoryStyle(categories.find(c => c.id == filters.category_id)).icon} {categories.find(c => c.id == filters.category_id)?.name}
                <button onClick={() => setFilters(p => ({ ...p, category_id: '' }))} style={D.chipX}>✕</button>
              </div>
            )}
            {filters.city_id && (
              <div style={D.chip}>
                🏙 {cities.find(c => c.id == filters.city_id)?.name}
                <button onClick={() => { setCityManual(true); setFilters(p => ({ ...p, city_id: '' })); }} style={D.chipX}>✕</button>
              </div>
            )}
            {filters.area_id && (
              <div style={D.chip}>
                📌 {areas.find(a => a.id == filters.area_id)?.name}
                <button onClick={() => setFilters(p => ({ ...p, area_id: '' }))} style={D.chipX}>✕</button>
              </div>
            )}
            <button onClick={clearAllFilters} style={{ ...D.chip, background: '#fee2e2', color: '#dc2626', border: 'none', fontWeight: 700 }}>
              Clear all ✕
            </button>
          </div>
        )}
      </header>

      {/* ── MAIN LAYOUT ── */}
      <div style={D.layout}>

        {/* DESKTOP SIDEBAR */}
        <aside style={D.sidebar} className="desktop-sidebar">
          <div style={D.sidebarHead}>Narrow Down Results</div>
          <FilterContent />
        </aside>

        {/* CONTENT */}
        <main style={{ flex: 1, minWidth: 0 }}>

          {/* Mobile filter bar */}
          <div style={D.mobileBar} className="show-mobile">
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {totalCount} results{userCity ? ` near ${userCity}` : ''}
            </span>
            <button onClick={() => setDrawerOpen(true)} style={D.filterToggle}>
              ⚙️ Filters {activeFilterCount > 0 && <span style={D.filterBadge}>{activeFilterCount}</span>}
            </button>
          </div>

          {/* Desktop result count */}
          <div className="desktop-sidebar" style={{ padding: '10px 20px', fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
            Showing <strong style={{ color: '#111827' }}>{totalCount} services</strong> {userCity && <>near <strong style={{ color: '#4f46e5' }}>{userCity}</strong></>}
          </div>

          {/* CARD GRID */}
          <div style={D.grid}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} />)
              : services.length === 0
                ? (
                  <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: 48, marginBottom: 8 }}>😕</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#374151', marginBottom: 6 }}>No services found</div>
                    <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>Try changing your search or removing filters</div>
                    <button onClick={clearAllFilters} style={{ background: '#4f46e5', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 12, fontWeight: 700, fontSize: 15 }}>
                      Show All Services
                    </button>
                  </div>
                )
                : services.map((s, i) => (
                  <div key={s.id} style={{ animation: `slideInUp 0.4s ease-out backwards`, animationDelay: `${Math.min(i * 0.05, 0.3)}s` }}>
                    <ServiceCard s={s} />
                  </div>
                ))
            }
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && !loading && (
            <div style={D.paginationRow}>
              <button
                onClick={() => { setCurrentPage(p => p - 1); fetchServices(currentPage - 1); }}
                disabled={currentPage === 1}
                style={{ ...D.pageBtn, opacity: currentPage === 1 ? 0.4 : 1 }}
              >
                ← Previous
              </button>
              <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 600 }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => { setCurrentPage(p => p + 1); fetchServices(currentPage + 1); }}
                disabled={currentPage === totalPages}
                style={{ ...D.pageBtn, opacity: currentPage === totalPages ? 0.4 : 1 }}
              >
                Next →
              </button>
            </div>
          )}
        </main>
      </div>

      {/* ── MOBILE FILTER DRAWER ────────────────────────────────── */}
      {drawerOpen && (
        <div style={D.overlay} onClick={() => setDrawerOpen(false)}>
          <div style={D.drawer} onClick={e => e.stopPropagation()}>
            <div style={D.drawerHeader}>
              <span style={{ fontWeight: 900, fontSize: 18 }}>⚙️ Filter Results</span>
              <button onClick={() => setDrawerOpen(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 10, padding: '6px 12px', fontSize: 16 }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '0 16px 16px' }}>
              <FilterContent />
            </div>
            <div style={{ padding: '16px', borderTop: '1px solid #f3f4f6' }}>
              <button onClick={() => setDrawerOpen(false)} style={D.drawerApply}>
                ✅ Show {totalCount} Results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   DESIGN TOKENS
══════════════════════════════════════════════════════ */
const D = {
  page: { minHeight: '100vh', background: '#f9fafb' },
  header: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '12px 16px 8px', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  brandTitle: { margin: 0, fontSize: 18, fontWeight: 900, color: '#111827', letterSpacing: '-0.5px' },
  locationLine: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6b7280', marginTop: 2, flexWrap: 'wrap' },

  /* search */
  searchBox: { display: 'flex', alignItems: 'center', gap: 4, background: '#f3f4f6', borderRadius: 10, padding: '4px 5px 4px 8px', border: '2px solid transparent', transition: '0.2s' },
  searchBoxFocused: { background: '#fff', borderColor: '#6366f1' },
  searchInput: { flex: 1, border: 'none', background: 'transparent', fontSize: 12, color: '#111827', fontFamily: 'Nunito', fontWeight: 600, minWidth: 0 },
  clearX: { background: 'none', border: 'none', fontSize: 12, color: '#9ca3af', padding: '2px 3px' },
  searchBtn: { background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' },
  sugBox: { position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.12)', zIndex: 200, overflow: 'hidden' },
  sugItem: { padding: '10px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600, color: '#374151', borderBottom: '1px solid #f9fafb', transition: '0.15s' },

  /* tabs */
  tabRow: { display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 3, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' },
  tabPill: { display: 'flex', alignItems: 'center', gap: 5, padding: '2px 12px', borderRadius: 50, border: '2px solid #e5e7eb', background: '#f9fafb', fontSize: 12, fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', transition: '0.15s' },
  tabPillActive: { background: '#eef2ff', borderColor: '#6366f1', color: '#4338ca' },

  /* chips */
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eef2ff', color: '#4338ca', padding: '4px 9px 4px 10px', borderRadius: 50, fontSize: 11, fontWeight: 700, border: '1.5px solid #c7d2fe' },
  chipX: { background: 'none', border: 'none', color: 'inherit', fontSize: 11, padding: '0 2px', marginLeft: 1, lineHeight: 1 },

  /* layout */
  layout: { display: 'flex', alignItems: 'flex-start' },

  /* sidebar */
  sidebar: { width: 220, background: '#fff', borderRight: '1px solid #e5e7eb', padding: '12px 8px', position: 'sticky', top: 0, height: '100vh', overflowY: 'auto', flexShrink: 0 },
  sidebarHead: { fontSize: 13, fontWeight: 900, color: '#111827', marginBottom: 8, paddingBottom: 6, borderBottom: '2px solid #f3f4f6' },
  filterSection: { marginBottom: 1 },
  filterLabel: { fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3, marginTop: 1 },
  filterDivider: { height: 1, background: '#f3f4f6', margin: '4px 0' },
  clearBtn: { width: '100%', background: '#fff1f2', color: '#e11d48', border: '1.5px solid #fecdd3', borderRadius: 8, padding: '6px', fontWeight: 700, fontSize: 11 },

  /* category tree */
  catBtn: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', border: '2px solid transparent', borderRadius: 10, padding: '2px 4px', fontSize: 12, transition: '0.15s', marginBottom: 1 },
  catChildBtn: { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderRadius: 6, padding: '3px 6px', fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 0.5 },
  badge: { fontSize: 9, padding: '1px 4px', borderRadius: 20, fontWeight: 700 },

  select: { width: '100%', padding: '6px 8px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#374151', background: '#fff', appearance: 'auto', marginTop: 3 },

  /* mobile */
  mobileBar: { display: 'none', padding: '10px 14px', background: '#fff', borderBottom: '1px solid #e5e7eb', justifyContent: 'space-between', alignItems: 'center' },
  filterToggle: { display: 'flex', alignItems: 'center', gap: 5, background: '#f9fafb', border: '2px solid #e5e7eb', borderRadius: 10, padding: '7px 12px', fontWeight: 700, fontSize: 12 },
  filterBadge: { background: '#4f46e5', color: '#fff', borderRadius: 50, padding: '1px 5px', fontSize: 10, fontWeight: 800 },

  /* grid */
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8, padding: '8px 12px 16px' },

  /* card */
  card: { background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: 14, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' },
  catChip: { display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 50, fontSize: 10, fontWeight: 800 },
  ratingChip: { background: '#fef3c7', color: '#d97706', borderRadius: 50, padding: '2px 8px', fontSize: 11, fontWeight: 800 },
  cardTitle: { fontSize: 14, fontWeight: 800, color: '#111827', margin: '8px 0 10px', lineHeight: 1.3 },
  vendorRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  avatar: { width: 32, height: 32, borderRadius: 50, background: 'linear-gradient(135deg, #6366f1, #a855f7)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14, flexShrink: 0 },
  vendorName: { fontSize: 12, fontWeight: 700, color: '#374151' },
  vendorLoc: { fontSize: 10, color: '#9ca3af', marginTop: 1 },
  cardFooter: { borderTop: '1.5px solid #f3f4f6', paddingTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  price: { fontSize: 16, fontWeight: 900, color: '#111827' },
  btnCall: { background: '#dcfce7', color: '#15803d', border: '2px solid #bbf7d0', borderRadius: 8, padding: '6px 10px', fontSize: 11, fontWeight: 800 },
  btnView: { background: '#fef3c7', color: '#b45309', border: '2px solid #fcd34d', borderRadius: 8, padding: '6px 10px', fontSize: 11, fontWeight: 800, cursor: 'pointer', transition: '0.15s' },
  btnWhatsapp: { background: '#dbeafe', color: '#1d4ed8', border: '2px solid #bfdbfe', borderRadius: 8, padding: '6px 10px', fontSize: 11, fontWeight: 800 },

  /* pagination */
  paginationRow: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, padding: '16px 0 24px' },
  pageBtn: { background: '#fff', border: '2px solid #e5e7eb', borderRadius: 10, padding: '8px 16px', fontWeight: 700, fontSize: 13, color: '#374151' },

  /* drawer */
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'flex-end' },
  drawer: { background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' },
  drawerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 14px 10px', borderBottom: '1px solid #f3f4f6' },
  drawerApply: { width: '100%', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 12, padding: '12px', fontWeight: 900, fontSize: 15 },
  /* ── GRID SYSTEM (Crucial for reducing width) ── */
  card: {
    background: '#fff',
    border: '1.5px solid #eef2ff',
    borderRadius: '14px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: '0.2s',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
  },

  cardTitle: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#1e293b',
    margin: '2px 0 4px',
    lineHeight: '1.3',
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    height: '32px',
  },

  descriptionText: {
    fontSize: '11px',
    color: '#64748b',
    margin: '0 0 6px 0',
    lineHeight: '1.5',
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    height: '28px',
  },

  metaInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
    borderTop: '1px dashed #e2e8f0',
    marginBottom: '4px'
  },

  metaItem: { fontSize: '10px', fontWeight: '700', color: '#94a3b8' },
  priceText: { fontSize: '14px', fontWeight: '900', color: '#0f172a' },

  cardFooter: {
    marginTop: 'auto',
    paddingTop: '6px',
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },

  vendorGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1, // Takes available space to keep name visible
    minWidth: 0 // Allows text truncation if space is extremely tight
  },

  avatarSmall: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    color: '#fff',
    fontSize: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '800',
    flexShrink: 0
  },

  vendorNameVisible: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#475569',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline-block'
  },

  actionButtonGroup: {
    display: 'flex',
    gap: '6px',
    flexShrink: 0
  },

  iconBtnBase: {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: '0.2s'
  },
  actionButtonGroup: {
    display: 'flex',
    gap: '4px', // Tighter gap to fit 3 buttons
    flexShrink: 0
  },

  iconBtnBase: {
    width: '30px', // Standardized size for all three
    height: '30px',
    borderRadius: '50%', // Circular border for all buttons
    border: '1px solid transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    outline: 'none'
  },

  // Color-coded button styles
  iconBtnView: {
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0'
  },

  iconBtnCall: {
    background: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0'
  },

  iconBtnWA: {
    background: '#ecfdf5', // True WhatsApp light green
    color: '#128c7e',
    border: '1px solid #d1fae5'
  },

  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '4px'
  },

  cardTitle: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#1e293b',
    lineHeight: '1.3',
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    flex: 1, // Title takes most space
    height: '36px',
  },

  titleEyeBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    background: '#f8fafc',
    color: '#6366f1',
    border: '1px solid #e2e8f0',
    flexShrink: 0,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },

  iconBtnCall: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
  iconBtnWA: { background: '#f0fdfa', color: '#0d9488', border: '1px solid #99f6e4' },

  ratingChip: { background: '#fffbeb', color: '#b45309', borderRadius: '50px', padding: '2px 8px', fontSize: '10px', fontWeight: '800' },
  catChip: { fontSize: '9px', fontWeight: '800', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase' }
};

// Map specific button colors
D.iconBtnCall = { ...D.iconBtnBase, background: '#dcfce7', color: '#15803d' };
D.iconBtnWA = { ...D.iconBtnBase, background: '#dbeafe', color: '#1d4ed8' };

export default Services;